import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  DirectionsCar as CarIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
} from "@mui/icons-material";
import { useAdmin } from "../context/AdminContext";
import {
  formatDate,
  getStatusColor,
  generateVehicleId,
  formatStatusLabel,
  getVehicleAssignmentState,
} from "../utils/helpers";
import Select from "../components/SafeSelect";
import { uploadProfileImage } from "../utils/profileImageUpload";
import carImage from "../assets/images/Bentley_Bentayga_Speed_US-spec_2.webp";
 
const VEHICLE_TYPES = [
  'Exotic Vehicle',
  'Prestige Sedan',
  'Executive Sedan',
  'Luxury SUV',
  'EV Sedan',
  'Luxury People Mover',
  'Stretch Limousine',
  'Executive Mini Bus',
  'Bus',
  'Coach',
];

const VEHICLE_MODELS = [
  { label: 'Bentley Bentayga', value: 'Bentley Bentayga' },
  { label: 'Mercedes G-Wagon', value: 'Mercedes G-Wagon' },
  { label: 'BMW 7 Series', value: 'BMW 7 Series' },
  { label: 'Mercedes S-Class', value: 'Mercedes S-Class' },
  { label: 'BMW 5 Series', value: 'BMW 5 Series' },
  { label: 'BMW X7', value: 'BMW X7' },
  { label: 'Tesla Model Y', value: 'Tesla Model Y' },
  { label: 'Mercedes V250D', value: 'Mercedes V250D' },
  { label: 'Ford Fairlane Stretch', value: 'Ford Fairlane Stretch' },
  { label: 'Mercedes Sprinter', value: 'Mercedes Sprinter' },
  { label: 'Mitsubishi Rosa', value: 'Mitsubishi Rosa' },
  { label: 'Yutong Coach', value: 'Yutong Coach' },
];

const getVehicleImageUrl = (vehicle = null) =>
  vehicle?.imageUrl ||
  vehicle?.vehicleImageUrl ||
  vehicle?.photoURL ||
  vehicle?.photoUrl ||
  "";

const resolveDriverByIdentifier = (driverList = [], identifier = "") => {
  if (!identifier) {
    return null;
  }

  return (
    driverList.find(
      (driver) =>
        driver.id === identifier ||
        driver.userId === identifier ||
        driver.uid === identifier
    ) || null
  );
};

const Warehouse = () => {
  const {
    vehicles,
    drivers,
    tasks,
    createVehicle,
    updateVehicle,
    deleteVehicle,
  } = useAdmin();
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewMode, setViewMode] = useState("card"); // 'card' or 'table'
  const [uploadingVehicleImage, setUploadingVehicleImage] = useState(false);

  const [formData, setFormData] = useState({
    vehicleId: "",
    vehicleType: "",
    model: "",
    year: "",
    licensePlate: "",
    capacity: "",
    imageUrl: "",
    status: "available",
  });

  const handleOpenDialog = (vehicle = null) => {
    if (vehicle) {
      setEditMode(true);
      setSelectedVehicle(vehicle);
      setFormData({
        vehicleId: vehicle.vehicleId || "",
        vehicleType: vehicle.vehicleType || "",
        model: vehicle.model || "",
        year: vehicle.year || "",
        licensePlate: vehicle.licensePlate || "",
        capacity: vehicle.capacity || "",
        imageUrl: getVehicleImageUrl(vehicle),
        status: vehicle.status || "available",
      });
    } else {
      setEditMode(false);
      setSelectedVehicle(null);
      setFormData({
        vehicleId: generateVehicleId(),
        vehicleType: "",
        model: "",
        year: "",
        licensePlate: "",
        capacity: "",
        imageUrl: "",
        status: "available",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedVehicle(null);
    setUploadingVehicleImage(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleVehicleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setUploadingVehicleImage(true);
      const imageUrl = await uploadProfileImage({
        file,
        folder: "vehicleImages",
        entityId:
          selectedVehicle?.id ||
          formData.vehicleId ||
          formData.licensePlate ||
          "new-vehicle",
      });

      setFormData((previousFormData) => ({
        ...previousFormData,
        imageUrl,
      }));
      showSnackbar("Vehicle image uploaded successfully", "success");
    } catch (error) {
      showSnackbar(error.message || "Failed to upload vehicle image", "error");
    } finally {
      setUploadingVehicleImage(false);
      event.target.value = "";
    }
  };

  const validateForm = () => {
    if (!formData.vehicleType.trim()) {
      showSnackbar("Vehicle type is required", "error");
      return false;
    }
    if (!formData.licensePlate.trim()) {
      showSnackbar("License plate is required", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (uploadingVehicleImage) {
      showSnackbar("Please wait for image upload to finish", "warning");
      return;
    }

    let result;
    if (editMode) {
      result = await updateVehicle(selectedVehicle.id, formData);
    } else {
      result = await createVehicle(formData);
    }

    if (result.success) {
      showSnackbar(
        `Vehicle ${editMode ? "updated" : "added"} successfully`,
        "success"
      );
      handleCloseDialog();
    } else {
      showSnackbar(result.error || "Operation failed", "error");
    }
  };

  const handleDelete = async (vehicleId) => {
    if (window.confirm("Are you sure you want to delete this vehicle?")) {
      const result = await deleteVehicle(vehicleId);
      if (result.success) {
        showSnackbar("Vehicle deleted successfully", "success");
      } else {
        showSnackbar(result.error || "Failed to delete vehicle", "error");
      }
    }
  };

  const handleToggleAvailability = async (vehicle) => {
    const newStatus =
      vehicle.status === "available" ? "unavailable" : "available";
    const result = await updateVehicle(vehicle.id, { status: newStatus });
    if (result.success) {
      showSnackbar(`Vehicle marked as ${newStatus}`, "success");
    } else {
      showSnackbar(result.error || "Failed to update status", "error");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const vehicleStateById = useMemo(() => {
    const stateMap = new Map();

    vehicles.forEach((vehicle) => {
      stateMap.set(
        vehicle.id,
        getVehicleAssignmentState(vehicle, tasks, { lockHours: 4 })
      );
    });

    return stateMap;
  }, [vehicles, tasks]);

  const getVehicleState = (vehicle = null) =>
    vehicleStateById.get(vehicle?.id) ||
    getVehicleAssignmentState(vehicle, tasks, { lockHours: 4 });

  const availableCount = vehicles.filter((vehicle) =>
    getVehicleState(vehicle).isAvailable
  ).length;

  const assignedCount = vehicles.filter((vehicle) => {
    const effectiveStatus = getVehicleState(vehicle).effectiveStatus;

    return (
      effectiveStatus === "assigned" ||
      effectiveStatus === "pending" ||
      effectiveStatus === "in-progress"
    );
  }).length;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Fleet Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <Tooltip title="Card View">
              <IconButton
                size="small"
                onClick={() => setViewMode("card")}
                sx={{
                  bgcolor: viewMode === "card" ? "#fff" : "transparent",
                  "&:hover": {
                    bgcolor: viewMode === "card" ? "#fff" : "#e0e0e0",
                  },
                }}
              >
                <CardViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Table View">
              <IconButton
                size="small"
                onClick={() => setViewMode("table")}
                sx={{
                  bgcolor: viewMode === "table" ? "#fff" : "transparent",
                  "&:hover": {
                    bgcolor: viewMode === "table" ? "#fff" : "#e0e0e0",
                  },
                }}
              >
                <ListViewIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Vehicle
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                  >
                    Total Vehicles
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  >
                    {vehicles.length}
                  </Typography>
                </Box>
                <CarIcon
                  sx={{ fontSize: 48, color: "#DD2424", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                  >
                    Available
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{ fontWeight: "bold", color: "#000000" }}
                  >
                    {availableCount}
                  </Typography>
                </Box>
                <CarIcon
                  sx={{ fontSize: 48, color: "#000000", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                  >
                    Assigned
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{ fontWeight: "bold", color: "#DD2424" }}
                  >
                    {assignedCount}
                  </Typography>
                </Box>
                <CarIcon
                  sx={{ fontSize: 48, color: "#DD2424", opacity: 0.3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Card View */}
      {viewMode === "card" ? (
        <Grid container spacing={3}>
          {vehicles.length === 0 ? (
            <Grid item size={{ xs: 12, md: 4, lg: 4 }}>
              <Paper sx={{ p: 6, textAlign: "center" }}>
                <CarIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No vehicles found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Add your first vehicle to get started
                </Typography>
              </Paper>
            </Grid>
          ) : (
            vehicles.map((vehicle) => {
              const vehicleState = getVehicleState(vehicle);
              const effectiveStatus = vehicleState.effectiveStatus;
              const vehicleImageUrl = getVehicleImageUrl(vehicle);
              const assignedDriverIdentifier = String(
                vehicle.assignedDriver || ""
              ).trim();
              const assignedDriver =
                !vehicleState.isAvailable && assignedDriverIdentifier
                  ? resolveDriverByIdentifier(drivers, assignedDriverIdentifier)
                  : null;

              return (
                <Grid item size={{ xs: 12, md: 4, lg: 4 }} key={vehicle.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    {/* Vehicle Image/Icon */}
                    <Box
                      sx={{
                        bgcolor: "#f5f5f5",
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        borderBottom: "1px solid #e0e0e0",
                        overflow: "hidden",
                      }}
                    >
                      <img 
                        src={vehicleImageUrl || carImage} 
                        alt={vehicle.vehicleType || "Vehicle"}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: vehicleImageUrl ? 'cover' : 'contain',
                        }}
                      />
                      <Chip
                        label={formatStatusLabel(effectiveStatus, "Unknown")}
                        color={getStatusColor(effectiveStatus)}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    {/* Vehicle Details */}
                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {vehicle.model ? vehicle.model : vehicle.vehicleType || "Vehicle"}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={vehicle.vehicleType || "N/A"}
                          size="small"
                          sx={{
                            mr: 1,
                            bgcolor: "#DD242420",
                            color: "#DD2424",
                            fontWeight: 500,
                          }}
                        />
                        {vehicle.year && (
                          <Chip
                            label={vehicle.year}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            License Plate:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {vehicle.licensePlate || "N/A"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Capacity:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {vehicle.capacity || "N/A"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Vehicle ID:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                          >
                            {vehicle.vehicleId || "N/A"}
                          </Typography>
                        </Box>

                        {vehicleState.isAvailable && vehicleState.availableUntil && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: "block" }}
                          >
                            Available until: {formatDate(vehicleState.availableUntil)}
                          </Typography>
                        )}

                        {vehicleState.blockingReason === "overdue_pending" && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ mt: 1, display: "block", fontWeight: 600 }}
                          >
                            Task pending: scheduled time already passed.
                          </Typography>
                        )}

                        {assignedDriver && (
                          <Box
                            sx={{
                              mt: 1,
                              pt: 1,
                              borderTop: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Assigned to:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {assignedDriver.name}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>

                    {/* Actions */}
                    <Box sx={{ p: 2, pt: 0, display: "flex", gap: 1 }}>
                      <Button
                        fullWidth
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(vehicle)}
                        sx={{
                          backgroundColor: "black",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#333", // slightly lighter on hover
                          },
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        fullWidth
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(vehicle.id)}
                        disabled={vehicleState.isTaskBlocked}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>
      ) : (
        /* Table View */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell>Vehicle ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>License Plate</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned Driver</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No vehicles found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                vehicles.map((vehicle) => {
                  const vehicleState = getVehicleState(vehicle);
                  const effectiveStatus = vehicleState.effectiveStatus;
                  const assignedDriverIdentifier = String(
                    vehicle.assignedDriver || ""
                  ).trim();
                  const assignedDriver =
                    !vehicleState.isAvailable && assignedDriverIdentifier
                      ? resolveDriverByIdentifier(drivers, assignedDriverIdentifier)
                      : null;

                  return (
                    <TableRow key={vehicle.id} hover>
                      <TableCell>{vehicle.vehicleId || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={vehicle.vehicleType || "N/A"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{vehicle.model || "N/A"}</TableCell>
                      <TableCell>{vehicle.year || "N/A"}</TableCell>
                      <TableCell>{vehicle.licensePlate || "N/A"}</TableCell>
                      <TableCell>{vehicle.capacity || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatusLabel(effectiveStatus, "Unknown")}
                          color={getStatusColor(effectiveStatus)}
                          size="small"
                          onClick={() =>
                            !vehicleState.isTaskBlocked &&
                            handleToggleAvailability(vehicle)
                          }
                          sx={{
                            cursor:
                              !vehicleState.isTaskBlocked
                                ? "pointer"
                                : "default",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {assignedDriver ? assignedDriver.name : "-"}
                      </TableCell>
                      <TableCell>{formatDate(vehicle.createdAt)}</TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenDialog(vehicle)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(vehicle.id)}
                              disabled={vehicleState.isTaskBlocked}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Vehicle Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit Vehicle" : "Add New Vehicle"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Vehicle Image
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: 180,
                  borderRadius: 1.5,
                  border: "1px solid #e0e0e0",
                  bgcolor: "#fafafa",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.25,
                }}
              >
                {formData.imageUrl ? (
                  <img
                    src={formData.imageUrl}
                    alt="Vehicle"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <img
                    src={carImage}
                    alt="Default vehicle"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  disabled={uploadingVehicleImage}
                >
                  {uploadingVehicleImage
                    ? "Uploading..."
                    : formData.imageUrl
                    ? "Change Image"
                    : "Upload Image"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={handleVehicleImageUpload}
                  />
                </Button>
                {Boolean(formData.imageUrl) && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() =>
                      setFormData((previousFormData) => ({
                        ...previousFormData,
                        imageUrl: "",
                      }))
                    }
                    disabled={uploadingVehicleImage}
                  >
                    Remove Image
                  </Button>
                )}
              </Box>
            </Box>

            <TextField
              label="Vehicle ID"
              name="vehicleId"
              value={formData.vehicleId}
              onChange={handleInputChange}
              fullWidth
              disabled={editMode}
            />
            
            <FormControl fullWidth required>
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                name="vehicleType"
                value={formData.vehicleType}
                label="Vehicle Type"
                onChange={handleInputChange}
              >
                {VEHICLE_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Model</InputLabel>
              <Select
                name="model"
                value={formData.model}
                label="Model"
                onChange={handleInputChange}
              >
                {VEHICLE_MODELS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Year"
              name="year"
              type="number"
              value={formData.year}
              onChange={handleInputChange}
              fullWidth
              placeholder="e.g., 2023"
            />
            <TextField
              label="License Plate"
              name="licensePlate"
              value={formData.licensePlate}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Capacity"
              name="capacity"
              value={formData.capacity}
              onChange={handleInputChange}
              fullWidth
              placeholder="e.g., 5 passengers, 1000 lbs"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                label="Status"
                onChange={handleInputChange}
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={uploadingVehicleImage}>
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Warehouse;
